import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import Drawer from '../../../components/Drawer'
import GrantContent from '../../../components/GrantContent'
import * as Permisos from '../../../utils/Permisos'
import { initials } from '../common/seller-common'
import { useSellerDrawerModel } from './seller-drawer-model'
import type { SellerDrawerProps } from './seller-drawer-model'

export type { SellerDrawerProps } from './seller-drawer-model'

const actionClass = 'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-k-line-strong bg-transparent px-3 text-[13px] font-bold text-k-ink no-underline hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const outlineBtnSx = {
    width: 'auto',
    minWidth: 'auto',
    flex: 'none',
    height: 40,
    padding: '0 16px',
    borderRadius: '12px',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1,
    backgroundColor: 'rgb(var(--k-surface))',
    color: 'rgb(var(--k-ink))',
    border: '1px solid rgb(var(--k-line))',
    '&:hover': {
        backgroundColor: 'rgb(var(--k-hover))',
        border: '1px solid rgb(var(--k-line))',
        boxShadow: 'none',
    },
} satisfies SxProps<Theme>

const dangerBtnSx: SxProps<Theme> = {
    ...outlineBtnSx,
    backgroundColor: 'rgb(var(--k-danger))',
    color: '#fff',
    border: 0,
    '&:hover': {
        backgroundColor: 'rgb(var(--k-danger))',
        filter: 'brightness(1.08)',
        boxShadow: 'none',
    },
}

const Field = ({ label, children, wide }: { label: string; children?: React.ReactNode; wide?: boolean }) => (
    <div className={wide ? 'sm:col-span-2' : ''}>
        <dt className="mb-0.5 text-xs text-k-muted">{label}</dt>
        <dd className="m-0 break-words text-sm font-semibold text-k-ink">{children || '—'}</dd>
    </div>
)

const SellerDrawer = (props: SellerDrawerProps) => {
    const { vendedorId, onClose, onEdit } = props
    const model = useSellerDrawerModel(props)
    const { vendedor, loading, name, activo, phone, email, whatsapp } = model

    return (
        <>
            <Drawer open={Boolean(vendedorId)} onClose={onClose} bare width={680}>
                <div className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-label={name ? `Detalle de ${name}` : 'Detalle del vendedor'}>
                    <div className="flex shrink-0 flex-col gap-4 border-b border-k-line px-6 pt-5 pb-4">
                        <div className="flex items-start gap-3.5">
                            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] text-xl font-extrabold text-white" style={{ background: 'linear-gradient(135deg, #1FC8C1, #3E6BE6 55%, #8B3FC9)' }}>
                                {initials(name)}
                            </span>
                            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <h2 className="m-0 truncate text-[21px] font-extrabold tracking-[-0.02em] text-k-ink">{name || 'Detalle del vendedor'}</h2>
                                {vendedor && !loading && (
                                    <>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] text-k-muted">
                                            <span>Expediente {vendedor.id}</span>
                                            {model.registroSpoken && <span>Alta {model.registroSpoken}</span>}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${activo ? 'bg-k-ok-soft text-k-ok' : 'bg-k-danger-soft text-k-danger-ink'}`}>{model.statusLabel || '—'}</span>
                                            {model.sexoLabel && <span className="rounded-full bg-k-accent-soft px-[9px] py-[3px] text-xs font-bold text-k-accent-ink">{model.sexoLabel}</span>}
                                        </div>
                                    </>
                                )}
                            </div>
                            <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                                <CloseIcon style={{ fontSize: 18 }} />
                            </button>
                        </div>

                        {vendedor && !loading && (
                            <div className="flex flex-wrap gap-2">
                                <a className={`${actionClass} ${phone ? '' : 'pointer-events-none opacity-40'}`} href={phone ? `tel:${phone}` : undefined} aria-disabled={!phone}>
                                    <PhoneOutlinedIcon style={{ fontSize: 15 }} />Llamar
                                </a>
                                <a className={`${actionClass} ${whatsapp ? '' : 'pointer-events-none opacity-40'}`} href={whatsapp ? `https://wa.me/${whatsapp}` : undefined} target="_blank" rel="noreferrer" aria-disabled={!whatsapp}>
                                    <ChatOutlinedIcon style={{ fontSize: 15 }} />WhatsApp
                                </a>
                                <a className={`${actionClass} ${email ? '' : 'pointer-events-none opacity-40'}`} href={email ? `mailto:${email}` : undefined} aria-disabled={!email}>
                                    <EmailOutlinedIcon style={{ fontSize: 15 }} />Correo
                                </a>
                                {activo && (
                                    <GrantContent permisos={[Permisos.VENDEDORES_W]}>
                                        <button type="button" className={actionClass} onClick={() => onEdit?.(vendedor.id)}>
                                            <EditOutlinedIcon style={{ fontSize: 15 }} />Editar
                                        </button>
                                    </GrantContent>
                                )}
                                {activo && (
                                    <GrantContent permisos={[Permisos.VENDEDORES_D]}>
                                        <button type="button" className={`${actionClass} text-k-danger-ink hover:bg-k-danger-soft`} onClick={model.askDelete} disabled={model.deleting}>
                                            <DeleteOutlinedIcon style={{ fontSize: 15 }} />Eliminar
                                        </button>
                                    </GrantContent>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-k-bg px-6 py-5">
                        {loading && (
                            <div className="flex flex-col items-center justify-center gap-4 py-12">
                                <RefreshIcon className="animate-spin text-k-primary" style={{ fontSize: 40 }} />
                                <p className="text-sm text-k-muted">Cargando detalle...</p>
                            </div>
                        )}

                        {model.error && !loading && (
                            <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                                <div className="flex items-center gap-2">
                                    <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 20 }} />
                                    <p className="text-sm text-k-danger-ink">{model.error}</p>
                                </div>
                                <Button className="mt-3" variant="contained" onClick={model.reload}>Reintentar</Button>
                            </div>
                        )}

                        {vendedor && !loading && (
                            <section className="flex flex-col gap-3 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
                                <h3 className="m-0 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-muted">Datos generales</h3>
                                <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                                    <Field label="Nombre" wide>{name}</Field>
                                    <Field label="RFC">{model.rfc}</Field>
                                    <Field label="Sexo">{model.sexoLabel}</Field>
                                    <Field label="Correo" wide>
                                        {email ? <a href={`mailto:${email}`} className="font-semibold text-k-primary no-underline">{email}</a> : '—'}
                                    </Field>
                                    <Field label="Teléfono">
                                        {phone ? <a href={`tel:${phone}`} className="font-semibold text-k-primary no-underline">{phone}</a> : '—'}
                                    </Field>
                                </dl>
                            </section>
                        )}
                    </div>
                </div>
            </Drawer>

            <Dialog open={model.confirmDelete} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar vendedor</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar al vendedor? Si continúa, se perderá toda la información del vendedor.</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.cancelDelete}>Cancelar</Button>
                    <Button variant="contained" sx={dangerBtnSx} onClick={model.deleteVendedor} disabled={model.deleting}>Eliminar</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default SellerDrawer
